import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'next-i18next';
import useLocalStorage from '@hooks/use-local-storage';
import Button from '@components/atoms/button/button';
import { CatalogContext } from '@context/catalog.context';
import { useAnalytics } from '@context/analytics.context';
import Icon from '@components/atoms/icon/icon';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { ApiResponse, ApiResponseError } from '@interfaces/api';
import Spinner from '@components/atoms/spinner/spinner';
import logger from '@helpers/utils/logger/client';
import SuccessModal from '@components/catalog/alerts/alerts-success-model/alerts-success-model';
import Link from '@components/atoms/link/link';
import clsx from 'clsx';
import useUser from '@hooks/user/use-user';
import { useBucket } from '@context/bucket.context';
import TagContainer from '@enums/tagContainer';
import { AlertCriterion } from '@interfaces/models/alertCriterion';
import useCreateAlert from '@components/catalog/alerts/alerts-create-manage-section/use-create-alert';
import CreateAlertButton from '@components/catalog/alerts/alerts-create-manage-section/create-alert-button';
import { AxiosError } from 'axios';
import useCheckAlertExistence from '@components/catalog/alerts/alerts-create-manage-section/use-check-alert-existence';
import { Alert } from '@interfaces/models/alert';
import styles from './alerts-create-manage-section.module.scss';

const ALERT_MANAGEMENT_URL = '/alerts/management';

interface AlertCreationSectionProps {
  alertCriteria: AlertCriterion[];
  setAlertError: (input: string) => void;
  customClassName?: string;
}

const AlertsCustomNotificationModal = dynamic(
  () => import('@components/catalog/alerts/alerts-custom-notification-modal/alerts-custom-notification-modal'),
  {
    ssr: false,
  },
);

const AlertCreateManageSection: React.FC<AlertCreationSectionProps> = (props) => {
  const { alertCriteria, setAlertError, customClassName } = props;

  const { t } = useTranslation();
  const { sendEvent } = useAnalytics();
  const { sendAnalyticEvent: sendAlertAnalyticEvent } = useAnalyticEvents('alerts');
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);
  const facets = useContextSelector(CatalogContext, (v) => v.facets);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const setCorrespondingAlertId = useContextSelector(CatalogContext, (v) => v.setCorrespondingAlertId);
  const { isAuthenticated } = useUser();
  const { isFeatureEnabled } = useBucket();
  const [showManageCta, setShowManageCta] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [isAlertCreating, setIsAlertCreating] = useState<boolean>(false);
  const [showAlertsCustomNotificationModal, setShowAlertsCustomNotificationModal] = useState<boolean>(false);
  const [alertId, setAlertId] = useState<string>(null);
  const [showedFakeDoorOnce, setShowedFakeDoorOnce] = useLocalStorage<number>(
    'alertsCustomNotificationModal',
    0,
    false,
  );

  useEffect(() => {
    setAlertError('');
  }, [alertCriteria, isAuthenticated, areMySizesEnabled]);

  useCheckAlertExistence({
    alertCriteria,
    onSuccess: (response) => {
      setShowManageCta(!!response.data?.exists);
      setCorrespondingAlertId(response?.data?.id ?? '');
    },
  });
  const onCreateSuccess = (data: ApiResponse<{ data: Alert }>) => {
    const enableSavedSearchesFakeDoorTest = isFeatureEnabled((f) => f.enableSavedSearchesFakeDoorTest, true);
    setAlertId(data?.data?.data?.id ?? '');
    sendAlertAnalyticEvent('alert_create');
    // If the feature flag is ON and the user has not seen the fake door yet
    // Then show the Fake Door Modal for custom notifications
    // AlertCreateManageSection is only shown on PLP and Campaign pages so we don't need the pageType check
    if (enableSavedSearchesFakeDoorTest && showedFakeDoorOnce < 1) {
      setShowAlertsCustomNotificationModal(true);
      setShowedFakeDoorOnce(() => showedFakeDoorOnce + 1);
    } else {
      setShowSuccessModal(true);
    }
    setShowManageCta(true);
    setAlertError('');
    setCorrespondingAlertId(data?.data?.data?.id ?? '');
    sendEvent({
      type: 'event',
      container: TagContainer.Analytics,
      payload: {
        event: 'globalAlert',
        product_brand: staticFilterGroups.brand && facets?.brand ? facets.brand[0]?.name : '',
        product_category: staticFilterGroups.categoryLvl0 && facets?.categoryLvl0 ? facets.categoryLvl0[0]?.name : '',
        product_univers: staticFilterGroups.universe && facets?.universe ? facets.universe[0]?.name : '',
        action: 'save_my_alert',
        category: 'alert',
      },
    });
  };

  const onCreateError = (error: AxiosError<{ errors: ApiResponseError }>) => {
    logger.error('alerts::createAnAlert::', error);
    // 4xx/5xx error
    const apiResponseError = error?.response?.data?.errors;
    if (apiResponseError) {
      setAlertError(apiResponseError.detail as string);
      if (apiResponseError.status === 409) {
        setShowManageCta(true);
      }
      return;
    }
    setAlertError(t('ALERT.GENERIC_ERROR'));
  };

  const { createAlertMutateFn } = useCreateAlert({
    onMutate: () => {
      setIsAlertCreating(true);
    },
    onCreateFinished: () => {
      setIsAlertCreating(false);
    },
    onCreateError,
    onCreateSuccess,
  });

  const handleCreateAlert = () => {
    const payload = {
      withPerso: areMySizesEnabled,
      category: 'newProduct' as const,
      criteria: alertCriteria,
    };
    createAlertMutateFn(payload);
  };

  const sendManageCtaTracking = (): void => {
    sendAlertAnalyticEvent('alert_manage');
  };

  const iconName = useMemo(() => {
    return showManageCta ? 'bookmark-filled-default' : 'bookmark-default';
  }, [showManageCta]);

  return (
    <>
      <div>
        {showManageCta ? (
          <Button
            disableDefaultStyling
            className={clsx(styles.alertCreationSection__link, customClassName)}
            data-vc-dd-action-name="manageAlertsFromPLP"
          >
            <Icon
              name={iconName}
              className={styles.alertCreationSection__link__icon}
              ariaHidden
            />
            <Link
              className={styles.alertCreationSection__manageLink}
              href={ALERT_MANAGEMENT_URL}
              onClick={sendManageCtaTracking}
            >
              {t('ALERT.BTN_MANAGE')}
            </Link>
          </Button>
        ) : (
          <CreateAlertButton
            buttonClass={clsx(styles.alertCreationSection__link, customClassName)}
            icon={
              isAlertCreating ? (
                <Spinner className={styles.alertCreationSection__link__loading} />
              ) : (
                <Icon
                  name={iconName}
                  className={styles.alertCreationSection__link__icon}
                  ariaHidden
                />
              )
            }
            onClick={handleCreateAlert}
            isAlertCreating={isAlertCreating}
            label={'ALERT.BTN_CREATE'}
          />
        )}
        <SuccessModal
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
        />
        <AlertsCustomNotificationModal
          open={showAlertsCustomNotificationModal}
          alertId={alertId}
          handleClose={() => setShowAlertsCustomNotificationModal(false)}
        />
      </div>
    </>
  );
};

export default AlertCreateManageSection;
